import useSharedClientForm, {
  ApiFieldName,
} from "@shared/features/onboarding/hooks/useSharedClientForm";
import { displayResultMessage, getAvoBotSource } from "utils/misc";
import { useWhitelistIPs } from "features/configuration/accounts/CreateAccount/WhitelistIPs";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { useContext, useEffect } from "react";
import { AvoContext } from "analytics/AvoProvider";
import { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { OnboardingStep } from "analytics/Avo";
import { ClientFormProps } from "features/configuration/common/types";
import useConfirmModal from "common/Modal/useConfirmModal";

const useClientForm = ({
  account,
  onSubmitSuccess,
  closeDrawer,
  form,
  isOnboarding,
  getErrorNotification,
  primaryExchange,
  getSuccessMessage,
}: ClientFormProps) => {
  const { avo } = useContext(AvoContext);
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { confirmModalContextHolder, openConfirmModal } = useConfirmModal({
    modalWidth: 600,
  });
  const { tryAgainLoading, quickConnectLoading } =
    useContext(QuickConnectContext) || {};

  useEffect(() => {
    avo?.createAccountView({
      botSource: getAvoBotSource(),
    });
  }, [avo]);

  useEffect(() => {
    if (tryAgainLoading && primaryExchange)
      avo?.connectExchangeTryAgainClicked({
        onboardingStep: isOnboarding
          ? OnboardingStep.CONNECT_EXCHANGE
          : undefined,
        clientExchange: primaryExchange,
      });
  }, [avo, tryAgainLoading, primaryExchange]);

  const onConnectToExchangeClicked = () => {
    if (isOnboarding && primaryExchange) {
      avo?.onboardingConnectToExchangeClicked({
        onboardingStep: OnboardingStep.CONNECT_EXCHANGE,
        clientExchange: primaryExchange,
        createClientIsQuickConnect: false,
      });
    }
  };

  const {
    isFetchingWhitelistIPs,
    isQuickConnectExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    handleExchangeChange,
    validateApiInfo: sharedValidateApiInfo,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    exchangesLoading,
    accountSubExchange,
    isHyperLiquid,
  } = useSharedClientForm({
    account,
    primaryExchange,
    onSubmitSuccess,
    closeDrawer,
    displayResultMessage,
    useWhitelistIPs,
    onConnectToExchangeClicked,
    getErrorNotification,
    useQuotaModal,
    openConfirmModal,
    getSuccessMessage,
    isOnboarding,
  });

  useEffect(() => {
    if (primaryExchange) {
      handleExchangeChange(primaryExchange);
    }
  }, [primaryExchange]);

  const validateApiInfo = (fieldName: ApiFieldName, fieldLabel: string) => {
    const errorMessage = sharedValidateApiInfo(
      form.getFieldsValue(),
      fieldName,
      fieldLabel
    );
    if (errorMessage) {
      return Promise.reject(new Error(errorMessage));
    }
    return Promise.resolve();
  };

  const isLoading = exchangesLoading || isFetchingWhitelistIPs;

  return {
    appTheme,
    isMobile,
    isQuickConnectExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    handleExchangeChange,
    validateApiInfo,
    isLoading,
    quickConnectLoading,
    openConfirmModal,
    confirmModalContextHolder,
    accountSubExchange,
    isHyperLiquid,
  };
};

export default useClientForm;
